import { ChessAppLayout } from "@/components/ChessAppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useSEO, generateEducationalJsonLd } from '@/hooks/useSEO';

const UserAgreement = () => {
  // SEO Configuration
  useSEO({
    title: "User Agreement - ChessHatch Community Guidelines",
    description: "ChessHatch User Agreement covering safety, educational use, community guidelines, and youth-focused policies for our chess learning platform created by Ved from Australia.",
    keywords: "chess user agreement, youth chess safety, chess community guidelines, chess education policies, young chess players",
    jsonLd: generateEducationalJsonLd("ChessHatch User Agreement", "Youth-focused chess education platform guidelines and community standards")
  });

  return (
    <ChessAppLayout>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4 text-teen-purple">User Agreement</h1>
          <p className="text-muted-foreground text-lg">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>1. Welcome to ChessHatch</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>
                Welcome to ChessHatch! This User Agreement governs your access to and use of our chess platform designed specifically for young people. By creating an account or using our services, you agree to these terms.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. Age Requirements and Parental Consent</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>ChessHatch is designed for users of all ages, with special consideration for young people:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>Users under 13 must have explicit parental consent</li>
                <li>Users between 13-17 should inform their parents about their account</li>
                <li>Parents may contact us at any time to review their child's account activity</li>
                <li>We recommend parental supervision for all young users</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>3. Account Creation and Management</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>When creating your ChessHatch account:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>Provide accurate and complete information</li>
                <li>Choose an appropriate username (no offensive language)</li>
                <li>Keep your password secure and don't share it</li>
                <li>You're responsible for all activity on your account</li>
                <li>Only create one account per person</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Educational Use and Learning</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>ChessHatch is an educational platform. You may:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>Use all features for learning and improving at chess</li>
                <li>Share your games and analysis with friends and teachers</li>
                <li>Participate in educational activities and tournaments</li>
                <li>Access puzzles, tutorials, and training materials</li>
                <li>Use screenshots and game records for educational purposes</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>5. Community Guidelines and Conduct</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>To maintain a positive learning environment for everyone:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>Be respectful and kind to all players</li>
                <li>Use appropriate language in all communications</li>
                <li>Help newer players learn and improve</li>
                <li>Report any inappropriate behavior immediately</li>
                <li>Follow the spirit of fair play and good sportsmanship</li>
                <li>No bullying, harassment, or discrimination of any kind</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>6. Fair Play and Game Rules</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>All players must commit to fair play:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>No use of chess engines or computer assistance during games</li>
                <li>No help from other people during rated games</li>
                <li>Play your own games - no account sharing</li>
                <li>Don't deliberately lose games to manipulate ratings</li>
                <li>Finish your games when possible - avoid abandoning games</li>
              </ul>
              <p className="mt-4">
                See our <strong>Fair Play Policy</strong> for detailed guidelines.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>7. Privacy Protection for Young Users</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>We take privacy seriously, especially for young users:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>We collect minimal personal information</li>
                <li>Game data is used only for education and improvement</li>
                <li>No personal information is shared with third parties</li>
                <li>Parents can request account information at any time</li>
                <li>Users can delete their accounts and data</li>
              </ul>
              <p className="mt-4">
                See our <strong>Privacy Policy</strong> for complete details.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>8. Safety and Reporting</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>Your safety is our priority:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>Never share personal information with other players</li>
                <li>Report any uncomfortable interactions immediately</li>
                <li>Use our blocking and reporting tools</li>
                <li>Contact us if you feel unsafe or encounter problems</li>
                <li>Parents should monitor their child's online interactions</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>9. Intellectual Property and Content</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>
                ChessHatch and its content, including the logo, puzzles, tutorials, and software, 
                are owned by ChessHatch and protected by copyright and other intellectual property laws. 
                You may use our content for personal learning but may not copy or redistribute it 
                without permission.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>10. Account Suspension and Termination</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>We may suspend or terminate accounts for:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>Violation of fair play rules</li>
                <li>Inappropriate behavior or communication</li>
                <li>Creating multiple accounts</li>
                <li>Any conduct that harms the community</li>
              </ul>
              <p className="mt-4">
                We prefer education over punishment and will work with users and parents to resolve issues when possible.
              </p>
            </CardContent>
          </Card>

          <Separator className="my-8" />

          <Card className="bg-teen-green/5 border-teen-green/20">
            <CardHeader>
              <CardTitle>Contact ChessHatch</CardTitle>
              <CardDescription>
                Questions about your account or this agreement?
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Created by Ved Oza, a 12-year-old chess enthusiast from Sydney, Australia. 
                If you have questions about this User Agreement or need help with your account, 
                please contact us through our support channels.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </ChessAppLayout>
  );
};

export default UserAgreement;